import React, { useMemo } from "react";
import { Box, Typography, Chip } from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  IoSchoolOutline,
  IoBusinessOutline,
  IoFlaskOutline,
} from "react-icons/io5";
import GlassCard from "components/GlassCard";
import { GRADIENTS } from "utils/designTokens";

// ---------------------------------------------------------------------------
// Parse investigator string
// Format: "Alexander Coon, MD — PRINCIPAL_INVESTIGATOR — Carondelet at St. Joesphs"
// ---------------------------------------------------------------------------
const parseInvestigator = (raw) => {
  const parts = raw.split("\u2014").map((s) => s.trim());
  if (parts.length < 3) {
    // Try regular dash fallback
    const fallback = raw.split(" - ").map((s) => s.trim());
    return {
      name: fallback[0] || raw,
      role: fallback[1] || "INVESTIGATOR",
      institution: fallback[2] || "Not specified",
    };
  }
  return {
    name: parts[0],
    role: parts[1],
    institution: parts[2],
  };
};

const formatRole = (role) => {
  if (!role) return "Investigator";
  return role
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const KOLCards = ({ trials }) => {
  // Extract, deduplicate, and aggregate KOLs from all trials
  const kols = useMemo(() => {
    if (!trials || trials.length === 0) return [];

    const kolMap = {};

    trials.forEach((trial) => {
      if (!trial.investigators || trial.investigators.length === 0) return;

      trial.investigators.forEach((investigatorStr) => {
        const parsed = parseInvestigator(investigatorStr);
        const key = parsed.name.toLowerCase();

        if (!kolMap[key]) {
          kolMap[key] = {
            name: parsed.name,
            role: parsed.role,
            institution: parsed.institution,
            trialIds: [],
            conditions: new Set(),
          };
        }

        // Aggregate trial IDs
        if (!kolMap[key].trialIds.includes(trial.nct_id)) {
          kolMap[key].trialIds.push(trial.nct_id);
        }

        // Aggregate conditions
        if (trial.conditions) {
          trial.conditions.forEach((c) => kolMap[key].conditions.add(c));
        }
      });
    });

    // Convert to array and sort by number of trials (most active first)
    return Object.values(kolMap)
      .map((kol) => ({
        ...kol,
        conditions: Array.from(kol.conditions),
      }))
      .sort((a, b) => b.trialIds.length - a.trialIds.length);
  }, [trials]);

  if (kols.length === 0) return null;

  return (
    <Box>
      {/* Section header */}
      <Box display="flex" alignItems="center" mb={0.5}>
        <IoSchoolOutline size={20} color="#A78BFA" />
        <Typography
          variant="h6"
          sx={{
            background: GRADIENTS.chartAccent,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontWeight: 700,
            fontSize: "1.2rem",
            ml: 1,
          }}
        >
          Key Opinion Leaders
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.8rem", mb: 2.5 }}
      >
        Principal investigators driving competitor clinical research
      </Typography>

      <Grid container spacing={3}>
        {kols.map((kol) => (
          <Grid item xs={12} md={6} lg={4} key={kol.name}>
            <KOLCard kol={kol} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Individual KOL Card
// ---------------------------------------------------------------------------
const KOLCard = ({ kol }) => (
  <GlassCard sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
    <Box
      p={2.5}
      sx={{
        position: "relative",
        zIndex: 1,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Name + Role */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              color: "rgba(255,255,255,0.92)",
              fontWeight: 700,
              fontSize: "0.95rem",
              lineHeight: 1.3,
            }}
          >
            {kol.name}
          </Typography>
        </Box>
        <Chip
          label={formatRole(kol.role)}
          size="small"
          sx={{
            backgroundColor: "rgba(96,165,250,0.12)",
            color: "#60A5FA",
            fontWeight: 700,
            fontSize: "0.6rem",
            height: "22px",
            border: "1px solid rgba(96,165,250,0.25)",
            flexShrink: 0,
          }}
        />
      </Box>

      {/* Institution */}
      <Box display="flex" alignItems="center" gap={0.7} mb={2}>
        <IoBusinessOutline size={14} color="rgba(255,255,255,0.4)" />
        <Typography
          variant="body2"
          sx={{
            color: "rgba(255,255,255,0.6)",
            fontSize: "0.8rem",
          }}
        >
          {kol.institution}
        </Typography>
      </Box>

      {/* Associated Trials */}
      <Box mb={1.5}>
        <Typography
          variant="caption"
          sx={{
            color: "rgba(255,255,255,0.4)",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            fontWeight: 600,
            fontSize: "0.6rem",
            display: "block",
            mb: 0.7,
          }}
        >
          Associated Trials
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={0.7}>
          {kol.trialIds.map((id) => (
            <Chip
              key={id}
              label={id}
              size="small"
              sx={{
                backgroundColor: "rgba(255,255,255,0.06)",
                color: "rgba(255,255,255,0.7)",
                fontSize: "0.65rem",
                height: "20px",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Research Areas */}
      {kol.conditions.length > 0 && (
        <Box mt="auto">
          <Box display="flex" alignItems="center" gap={0.5} mb={0.7}>
            <IoFlaskOutline size={13} color="rgba(255,255,255,0.4)" />
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.4)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontWeight: 600,
                fontSize: "0.6rem",
              }}
            >
              Research Areas
            </Typography>
          </Box>
          <Box display="flex" flexWrap="wrap" gap={0.7}>
            {kol.conditions.map((condition) => (
              <Chip
                key={condition}
                label={condition}
                size="small"
                sx={{
                  backgroundColor: "rgba(167,139,250,0.1)",
                  color: "rgba(167,139,250,0.8)",
                  fontSize: "0.65rem",
                  height: "20px",
                  border: "1px solid rgba(167,139,250,0.2)",
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  </GlassCard>
);

export default KOLCards;
